import { UNIQUE_DEVICE_ID } from 'constants/authConstants';
import { IUnit } from 'interfaces/IInput';
import { MBase, MWriteBody } from 'interfaces/IMasterDb';

/**
 * This browser's device id. Falls back to the app-wide id (as other writers
 * use) when none is stored, since the API rejects a null sourceId.
 */
export const readDeviceId = (): string => {
  try {
    return localStorage.getItem(UNIQUE_DEVICE_ID) ?? UNIQUE_DEVICE_ID;
  } catch {
    return UNIQUE_DEVICE_ID;
  }
};

interface BuildMBaseParams {
  id: string;
  orgId: string;
  /** Omit for MOrganisation — it inherits BaseCollection, not MBaseCollection. */
  globalId?: string;
  deviceId: string | null;
  now?: string;
}

// Every base key is sent on Create: the server stores only the keys present,
// so a missing isActive hides the record and a missing dateModified makes every
// later Update a silent no-op. People fields are null (the server owns them).
export const buildMBase = ({
  id,
  orgId,
  globalId,
  deviceId,
  now = new Date().toISOString(),
}: BuildMBaseParams): MBase => ({
  id,
  orgId,
  ...(globalId !== undefined && { globalId }),
  isActive: true,
  version: 1,
  dateCreated: now,
  dateModified: now,
  creatorId: null,
  snapCreator: null,
  modifierId: null,
  snapModifier: null,
  sourceId: deviceId,
});

// Update keeps the stored identity (id, orgId, globalId, version) and only
// moves dateModified forward.
export const buildMUpdateBase = (
  existing: MBase<unknown>,
  deviceId: string | null,
  now: string = new Date().toISOString()
): MBase => ({
  id: existing.id,
  orgId: existing.orgId,
  globalId: existing.globalId,
  isActive: true,
  version: existing.version ?? 1,
  dateCreated: existing.dateCreated ?? now,
  dateModified: now,
  creatorId: null,
  snapCreator: null,
  modifierId: null,
  snapModifier: null,
  sourceId: deviceId,
});

export const buildMWriteBody = (
  base: MBase,
  fields: object
): MWriteBody => ({ ...base, ...fields });

// Latest dateModified first; documents without a date sort last.
export const sortByDateModifiedDesc = <T extends { dateModified?: string }>(
  items: T[]
): T[] =>
  [...items].sort(
    (a, b) =>
      (Date.parse(b.dateModified ?? '') || 0) -
      (Date.parse(a.dateModified ?? '') || 0)
  );

export interface MProductDefaults {
  rate?: number | null;
  rateUnit?: IUnit | null;
  price?: number | null;
  cost?: number | null;
  operationUnit?: IUnit | null;
}

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const isSameUnit = (a?: IUnit | null, b?: IUnit | null): boolean => {
  if (!a || !b) return a === b;
  return a.globalId && b.globalId
    ? a.globalId === b.globalId
    : a.unit === b.unit;
};

const UNIT_KEYS = ['rateUnit', 'operationUnit'] as const;
const NUMBER_KEYS = ['rate', 'price', 'cost'] as const;

/**
 * Fields in `next` (the row) that differ from `current` (the merged default).
 * Never returns null/empty values: null on an M document hides the G default.
 */
export const pickChangedDefaults = (
  current: MProductDefaults,
  next: MProductDefaults
): MProductDefaults => {
  const changed: MProductDefaults = {};

  NUMBER_KEYS.forEach((key) => {
    const value = next[key];
    if (isNumber(value) && value !== current[key]) changed[key] = value;
  });

  UNIT_KEYS.forEach((key) => {
    const value = next[key];
    if (value?.unit && !isSameUnit(current[key], value)) changed[key] = value;
  });

  return changed;
};
